import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import { type ComponentProps, forwardRef, type PropsWithChildren } from "react";
import Select, {
  type GroupBase,
  type Props as SelectProps
} from "react-select";

type OrderDetailProps = {
  icon: IconProp;
  name: string;
  required?: boolean;
  errorMessage?: string;
};

export const OrderInstructionItem = ({
  icon,
  name,
  children,
  errorMessage
}: PropsWithChildren<OrderDetailProps>) => {
  const handleClick = (event: React.MouseEvent) => {
    if (event.target instanceof HTMLInputElement) return;

    const input = event.currentTarget.querySelector("input");

    if (input) {
      // If the input is a select, don't focus on it
      const attributeRole = input.getAttribute("role");
      if (attributeRole === "combobox") {
        return;
      }

      input.focus();
    }
  };

  return (
    <div onClick={handleClick}>
      <div
        className={clsx(
          "flex w-full flex-1 flex-row items-center justify-between gap-5 rounded-xl bg-kiosk-background-secondary p-6 text-kiosk-text-secondary",
          "cursor-text",
          {
            "border-2 border-kiosk-text-error": Boolean(errorMessage)
          }
        )}
      >
        <div className="flex flex-row items-center gap-4">
          <FontAwesomeIcon icon={icon} size="xl" />
          <div className="flex flex-row gap-2 text-4xl font-semibold">
            <span>{name}</span>
          </div>
        </div>

        <div className="ml-16 flex flex-1">{children}</div>
      </div>

      {errorMessage ? (
        <span className="mt-1 text-2xl text-kiosk-text-error">
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
};

const dismissKeyboardOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === "Enter") {
    (e.target as HTMLInputElement).blur();
  }
};

export const OrderInstructionInput = forwardRef<
  HTMLInputElement,
  ComponentProps<"input">
>((props, ref) => {
  return (
    <input
      ref={ref}
      enterKeyHint="done"
      onKeyDown={dismissKeyboardOnEnter}
      className={clsx(
        "w-full resize-none bg-transparent text-right text-4xl font-bold text-kiosk-text-primary outline-none placeholder:font-normal",
        props.required
          ? "placeholder:text-kiosk-text-error"
          : "placeholder:text-kiosk-text-secondary"
      )}
      {...props}
    />
  );
});

OrderInstructionInput.displayName = "OrderInstructionInput";

export const OrderInstructionSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: SelectProps<Option, IsMulti, Group>
) => {
  return (
    <Select
      classNames={{
        control: () => "text-4xl font-bold",
        menu: () => "text-4xl font-semibold",
        container: () => "w-full text-end",
        option: (state) =>
          clsx("px-4 py-5 text-center", {
            "text-kiosk-text-primary": state.isSelected
          })
      }}
      styles={{
        control: (base) => ({
          ...base,
          backgroundColor: "transparent",
          borderColor: "transparent",
          boxShadow: "none",
          "&:hover": {
            borderColor: "transparent",
            boxShadow: "none"
          }
        }),
        dropdownIndicator: (base) => ({
          ...base,
          padding: 8
        }),
        option: () => ({}),
        menu: (base) => ({
          ...base,
          right: 0,
          borderRadius: "1.5rem", // rounded-2xl
          minWidth: "18rem", // w-72
          width: "fit-content"
        })
      }}
      components={{ IndicatorSeparator: () => null }}
      isSearchable={false}
      {...props}
    />
  );
};

OrderInstructionSelect.displayName = "OrderInstructionSelect";
